/**
 * SavedViewsDropdown Component
 * 
 * A reusable dropdown for saving and loading dashboard filter configurations.
 * Can be added to any dashboard that supports filters.
 * 
 * Usage:
 * <SavedViewsDropdown 
 *   dashboardType="sales"
 *   currentFilters={{ timeRange: 'month', selectedRep: 'all' }}
 *   onLoadView={(filters) => setFilters(filters)}
 * />
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Bookmark, 
  BookmarkPlus, 
  Check, 
  Star, 
  StarOff,
  Share2, 
  Trash2, 
  ChevronDown,
  Users,
  Building2,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHARE_SCOPE_OPTIONS = [
  { value: 'personal', label: 'Only Me', icon: User, description: 'Private - only you can see this view' },
  { value: 'team', label: 'My Team', icon: Users, description: 'Share with your team members' },
  { value: 'company', label: 'Company', icon: Building2, description: 'Share with entire company' },
];

export const SavedViewsDropdown = ({ 
  dashboardType, 
  currentFilters, 
  onLoadView,
  className = '' 
}) => {
  const { user } = useAuth();
  const [savedViews, setSavedViews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [shareScope, setShareScope] = useState('personal');
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [activeViewId, setActiveViewId] = useState(null);

  const userId = user?.id || user?.user_id;

  useEffect(() => {
    if (userId) {
      fetchSavedViews();
      loadDefaultView();
    }
  }, [dashboardType, userId]);

  const fetchSavedViews = async () => {
    if (!userId) return;
    try {
      const response = await axios.get(`${API}/saved-views`, {
        params: { dashboard_type: dashboardType, user_id: userId }
      });
      setSavedViews(response.data || []);
    } catch (error) {
      console.error('Error fetching saved views:', error);
    }
  };

  const loadDefaultView = async () => {
    if (!userId) return;
    try {
      const response = await axios.get(`${API}/saved-views/default/${dashboardType}`, {
        params: { user_id: userId }
      });
      if (response.data && response.data.filters) {
        setActiveViewId(response.data.id);
        onLoadView(response.data.filters);
        toast.success(`Loaded default view: ${response.data.name}`);
      }
    } catch (error) {
      // No default view is fine
    }
  };

  const handleSaveView = async () => {
    if (!newViewName.trim()) {
      toast.error('Please enter a name for the view');
      return;
    }
    if (!userId) {
      toast.error('Please log in to save views');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/saved-views?user_id=${userId}`, {
        name: newViewName,
        dashboard_type: dashboardType,
        filters: currentFilters,
        share_scope: shareScope,
        is_default: setAsDefault
      });
      
      toast.success(`View "${newViewName}" saved!`);
      setSaveDialogOpen(false);
      setNewViewName('');
      setShareScope('personal');
      setSetAsDefault(false);
      fetchSavedViews();
      setActiveViewId(response.data.id);
    } catch (error) {
      toast.error('Failed to save view');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadView = (view) => {
    setActiveViewId(view.id);
    onLoadView(view.filters);
    toast.success(`Loaded view: ${view.name}`);
  };

  const handleSetDefault = async (view) => {
    try {
      await axios.post(`${API}/saved-views/${view.id}/set-default?user_id=${userId}`);
      toast.success(`"${view.name}" set as default`);
      fetchSavedViews();
    } catch (error) {
      toast.error('Failed to set default');
    }
  };

  const handleRemoveDefault = async (view) => {
    try {
      await axios.post(`${API}/saved-views/${view.id}/remove-default?user_id=${userId}`);
      toast.success('Default removed');
      fetchSavedViews();
    } catch (error) {
      toast.error('Failed to remove default');
    }
  };

  const handleDeleteView = async (view) => {
    if (!window.confirm(`Delete view "${view.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/saved-views/${view.id}?user_id=${userId}`);
      toast.success('View deleted');
      if (activeViewId === view.id) {
        setActiveViewId(null);
      }
      fetchSavedViews();
    } catch (error) {
      toast.error('Failed to delete view');
    }
  };

  const activeView = savedViews.find(v => v.id === activeViewId);
  const personalViews = savedViews.filter(v => v.share_scope === 'personal');
  const sharedViews = savedViews.filter(v => v.share_scope !== 'personal');

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className={`gap-2 ${className}`}
            data-testid="saved-views-dropdown"
          >
            <Bookmark className="h-4 w-4" />
            {activeView ? activeView.name : 'Saved Views'}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {/* Save Current View */}
          <DropdownMenuItem 
            onClick={() => setSaveDialogOpen(true)}
            className="gap-2 cursor-pointer"
            data-testid="save-current-view"
          >
            <BookmarkPlus className="h-4 w-4" />
            Save Current View
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          {/* Personal Views */}
          {personalViews.length > 0 && (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-2">
                <User className="h-3 w-3" /> My Views
              </DropdownMenuLabel>
              {personalViews.map(view => (
                <DropdownMenuItem 
                  key={view.id}
                  className="flex items-center justify-between group cursor-pointer"
                  onClick={() => handleLoadView(view)}
                >
                  <div className="flex items-center gap-2">
                    {activeViewId === view.id && <Check className="h-4 w-4 text-green-500" />}
                    <span className={activeViewId === view.id ? 'font-medium' : ''}>{view.name}</span>
                    {view.is_default && <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                    {view.is_default ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemoveDefault(view); }}
                        className="p-1 hover:bg-slate-700 rounded"
                        title="Remove default"
                      >
                        <StarOff className="h-3 w-3" />
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSetDefault(view); }}
                        className="p-1 hover:bg-slate-700 rounded"
                        title="Set as default"
                      >
                        <Star className="h-3 w-3" />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteView(view); }}
                      className="p-1 hover:bg-red-600 rounded"
                      title="Delete view"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}
          
          {/* Shared Views */}
          {sharedViews.length > 0 && (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-2">
                <Share2 className="h-3 w-3" /> Shared Views
              </DropdownMenuLabel>
              {sharedViews.map(view => (
                <DropdownMenuItem 
                  key={view.id}
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => handleLoadView(view)}
                >
                  <div className="flex items-center gap-2">
                    {activeViewId === view.id && <Check className="h-4 w-4 text-green-500" />}
                    <span className={activeViewId === view.id ? 'font-medium' : ''}>{view.name}</span>
                    {view.share_scope === 'team' && <Users className="h-3 w-3 text-blue-400" />}
                    {view.share_scope === 'company' && <Building2 className="h-3 w-3 text-purple-400" />}
                  </div>
                  <span className="text-xs text-muted-foreground">{view.created_by_name}</span>
                </DropdownMenuItem>
              ))}
            </>
          )}
          
          {savedViews.length === 0 && (
            <div className="py-4 text-center text-sm text-muted-foreground">
              No saved views yet
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Save View Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Current View</DialogTitle>
            <DialogDescription>
              Save your current filter settings as a reusable view
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="view-name">View Name</Label>
              <Input
                id="view-name"
                placeholder="e.g., Q1 Sales Overview"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                data-testid="view-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Share With</Label>
              <Select value={shareScope} onValueChange={setShareScope}>
                <SelectTrigger data-testid="share-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SHARE_SCOPE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <option.icon className="h-4 w-4" />
                        <div>
                          <div>{option.label}</div>
                          <div className="text-xs text-muted-foreground">{option.description}</div>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label>Set as Default</Label>
                <p className="text-xs text-muted-foreground">
                  Automatically load this view when opening the dashboard
                </p>
              </div>
              <Switch
                checked={setAsDefault}
                onCheckedChange={setSetAsDefault}
                data-testid="set-default-switch"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveView} disabled={loading} data-testid="save-view-btn">
              {loading ? 'Saving...' : 'Save View'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SavedViewsDropdown;
